import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, ImageOff, Trash2, RefreshCw } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";

/**
 * Uploads a workspace logo directly via multipart to POST /api/orgs/logo.
 *
 * Props:
 *  - logoUrl:  current brand_logo_url on the org (may be a public /api/orgs/{id}/logo?v=... path or a hosted URL)
 *  - logoPath: the storage path if the current logo was uploaded (used to decide whether the Remove button appears)
 *  - onChanged: async callback fired after upload/delete; receives the updated org record
 */
export default function BrandingLogo({ logoUrl, logoPath, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type)) {
      toast.error("Unsupported file", { description: "Use PNG, JPEG, WEBP or SVG." });
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Too large", { description: "Logo must be under 2MB." });
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/orgs/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo updated");
      await onChanged?.(data);
    } catch (e) {
      toast.error("Upload failed", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the workspace logo?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/orgs/logo");
      toast.success("Logo removed");
      await onChanged?.(data);
    } catch (e) {
      toast.error("Could not remove", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  // Resolve the preview URL: if it's a relative /api/... path, prefix the backend host.
  const previewSrc = logoUrl
    ? (logoUrl.startsWith("http") ? logoUrl : `${BACKEND}${logoUrl}`)
    : null;

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) uploadFile(f);
      }}
      data-testid="branding-logo-dropzone"
      className={`rounded-xl border border-dashed p-5 transition ${
        dragOver ? "border-teal-500 bg-teal-50/60" : "border-slate-300 bg-slate-50/50"
      }`}
    >
      <div className="flex items-center gap-5 flex-wrap">
        <div className="h-20 w-20 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
          {previewSrc ? (
            <img
              src={previewSrc}
              alt="Workspace logo"
              className="h-full w-full object-contain"
              data-testid="branding-logo-preview"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <ImageOff className="h-6 w-6 text-slate-300" />
          )}
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm font-semibold text-slate-900">
            {previewSrc ? "Update your workspace logo" : "Upload your workspace logo"}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Drop a file here, or pick one below. PNG, JPEG, WEBP or SVG · square works best · max 2MB.
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              hidden
              data-testid="branding-logo-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadFile(f);
                e.target.value = "";
              }}
            />
            <Button
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              data-testid="branding-logo-upload-btn"
              className="rounded-full h-9 px-4 bg-teal-700 hover:bg-teal-800"
            >
              {previewSrc ? (
                <><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Replace</>
              ) : (
                <><Upload className="h-3.5 w-3.5 mr-1.5" /> Upload logo</>
              )}
            </Button>
            {logoPath && (
              <Button
                variant="outline"
                onClick={removeLogo}
                disabled={busy}
                data-testid="branding-logo-remove-btn"
                className="rounded-full h-9 px-4 border-rose-200 text-rose-700 hover:bg-rose-50"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
              </Button>
            )}
            {busy && <span className="text-xs text-slate-500">Working…</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
